import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import { toast } from "sonner";
import { DisclaimerFooter } from "@/components/Disclaimer";
import { usePeriod, isDateInPeriod } from "@/hooks/usePeriod";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from "recharts";
import { SectionActionsToolbar } from "@/components/SectionActions";



const MOOD_LEVELS = [
  { level: 1, emoji: "😢", label: "Molto male", color: "bg-red-100 border-red-300" },
  { level: 2, emoji: "😕", label: "Male", color: "bg-orange-100 border-orange-300" },
  { level: 3, emoji: "😐", label: "Neutro", color: "bg-yellow-100 border-yellow-300" },
  { level: 4, emoji: "🙂", label: "Bene", color: "bg-green-100 border-green-300" },
  { level: 5, emoji: "😊", label: "Molto bene", color: "bg-emerald-100 border-emerald-300" }
];

const MoodPage = () => {
  const { periodStartDate, getSectionPeriod } = usePeriod();
  const [moods, setMoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [currentMood, setCurrentMood] = useState({ level: null, note: "" });
  const [showAllHistory, setShowAllHistory] = useState(false);

  const monthStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, "0")}`;
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfWeek = (new Date(year, month, 1).getDay() + 6) % 7;
  const sectionPeriod = getSectionPeriod("moods");
  const effectivePeriod = showAllHistory ? null : (sectionPeriod || periodStartDate);

  useEffect(() => {
    fetchMoods();
  }, [currentMonth]);

  useEffect(() => {
    const dayMood = moods.find(m => m.date === selectedDate);
    if (dayMood) {
      setCurrentMood({ level: dayMood.level, note: dayMood.note || "" });
    } else {
      setCurrentMood({ level: null, note: "" });
    }
  }, [selectedDate, moods]);

  const fetchMoods = async () => {
    setLoading(true);
    const startDate = `${monthStr}-01`;
    const endDate = `${monthStr}-${daysInMonth}`;
    try {
      const response = await api.get(`/moods`, {
        params: { start_date: startDate, end_date: endDate }
      });
      setMoods(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Error fetching moods:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMoodSelect = async (level) => {
    setCurrentMood({ ...currentMood, level });
    try {
      await api.post(`/moods`, {
        date: selectedDate,
        level,
        note: currentMood.note
      });
      toast.success("Umore salvato!");
      fetchMoods();
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleNoteSave = async () => {
    if (!currentMood.level) {
      toast.error("Seleziona prima un umore");
      return;
    }
    try {
      await api.post(`/moods`, {
        date: selectedDate,
        level: currentMood.level,
        note: currentMood.note
      });
      toast.success("Nota salvata!");
      fetchMoods();
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  const getMoodForDay = (day) => {
    const date = `${monthStr}-${String(day).padStart(2, "0")}`;
    return moods.find(m => m.date === date);
  };

  const formatMonth = () => {
    return currentMonth.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
  };

  const formatSelectedDate = () => {
    return new Date(selectedDate).toLocaleDateString("it-IT", {
      weekday: "long",
      day: "numeric",
      month: "long"
    });
  };

  // Calculate mood stats - filtered by period
  const filteredMoods = useMemo(() => 
    moods.filter(m => !effectivePeriod || isDateInPeriod(m.date, effectivePeriod)),
    [moods, effectivePeriod]
  );
  
  const moodStats = useMemo(() => 
    MOOD_LEVELS.map(m => ({
      ...m,
      count: filteredMoods.filter(mood => mood.level === m.level).length
    })),
    [filteredMoods]
  );

  const averageMood = useMemo(() => 
    filteredMoods.length > 0 
      ? (filteredMoods.reduce((sum, m) => sum + m.level, 0) / filteredMoods.length).toFixed(1)
      : "-",
    [filteredMoods]
  );

  // Memoized chart data
  const moodChartData = useMemo(() => 
    filteredMoods
      .sort((a, b) => a.date.localeCompare(b.date))
      .map(m => ({
        date: new Date(m.date).getDate(),
        level: m.level,
        emoji: MOOD_LEVELS[m.level - 1]?.emoji
      })),
    [filteredMoods]
  );

  // Check if a day is in the period
  const isDayInPeriod = (day) => {
    if (showAllHistory) return true;
    const date = `${monthStr}-${String(day).padStart(2, "0")}`;
    return isDateInPeriod(date, effectivePeriod);
  };

  const WEEKDAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

  return (
    <div className="space-y-8 animate-fade-in" data-testid="mood-page">
      {/* Period Banner */}
      {effectivePeriod && (
        <div className="bg-muted/50 border border-border rounded-sm p-3 flex items-center gap-2 text-sm">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <span className="text-muted-foreground">
            Visualizzando dati dal <span className="font-medium text-foreground">{new Date(effectivePeriod).toLocaleDateString('it-IT')}</span>
          </span>
        </div>
      )}

      {/* Section Actions Toolbar */}
      <SectionActionsToolbar
        section="moods"
        currentDate={selectedDate}
        showAll={showAllHistory}
        onShowAllChange={setShowAllHistory}
        onDataChanged={fetchMoods}
      />

      {/* Header */}
      <div>
        <h1 className="page-title">Umore</h1>
        <p className="page-subtitle">Traccia come ti senti ogni giorno</p>
      </div>

      {/* Month Navigation */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigateMonth(-1)} data-testid="prev-month-btn">
          <ChevronLeft className="w-4 h-4" />
          <span className="hidden sm:inline ml-1">Mese prec.</span>
        </Button>
        <span className="font-heading text-sm sm:text-lg capitalize">{formatMonth()}</span>
        <Button variant="ghost" size="sm" onClick={() => navigateMonth(1)} data-testid="next-month-btn">
          <span className="hidden sm:inline mr-1">Mese succ.</span>
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Calendar */}
        <Card className="lg:col-span-2 border border-border shadow-sm rounded-sm" data-testid="mood-calendar">
          <CardContent className="p-3 sm:p-6">
            {/* Weekday headers */}
            <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-2 sm:mb-4">
              {WEEKDAYS.map(day => (
                <div key={day} className="text-center text-[10px] sm:text-xs font-medium text-muted-foreground py-1 sm:py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1 sm:gap-2">
              {/* Empty cells for days before first day of month */}
              {Array.from({ length: firstDayOfWeek }).map((_, i) => (
                <div key={`empty-${i}`} className="aspect-square"></div>
              ))}

              {/* Days of month */}
              {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                const date = `${monthStr}-${String(day).padStart(2, "0")}`;
                const dayMood = getMoodForDay(day);
                const isSelected = date === selectedDate;
                const isToday = date === new Date().toISOString().split("T")[0];
                const moodData = dayMood ? MOOD_LEVELS.find(m => m.level === dayMood.level) : null;

                return (
                  <button
                    key={day}
                    onClick={() => setSelectedDate(date)}
                    className={`
                      aspect-square rounded-sm border-2 flex flex-col items-center justify-center transition-all
                      ${isSelected ? "ring-2 ring-foreground ring-offset-1 sm:ring-offset-2" : ""}
                      ${isToday ? "border-foreground" : "border-border"}
                      ${moodData ? moodData.color : "bg-background hover:bg-muted"}
                    `}
                    data-testid={`calendar-day-${day}`}
                  >
                    <span className={`text-[10px] sm:text-xs font-mono ${isToday ? "font-bold" : ""}`}>{day}</span>
                    {moodData && <span className="text-sm sm:text-lg">{moodData.emoji}</span>}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Mood Selector & Stats */}
        <div className="space-y-6">
          {/* Selected Day Mood */}
          <Card className="border border-border shadow-sm rounded-sm" data-testid="mood-selector">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-lg capitalize">
                {formatSelectedDate()}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-5 gap-2">
                {MOOD_LEVELS.map(({ level, emoji, label }) => (
                  <button
                    key={level}
                    onClick={() => handleMoodSelect(level)}
                    className={`
                      aspect-square rounded-sm border-2 flex items-center justify-center text-2xl transition-all hover:scale-105
                      ${currentMood.level === level ? "border-foreground bg-foreground/10 scale-105" : "border-border"}
                    `}
                    title={label}
                    data-testid={`mood-level-${level}`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>

              <div>
                <label className="text-sm text-muted-foreground block mb-2">
                  Note (opzionale)
                </label>
                <Textarea
                  data-testid="mood-note-input"
                  value={currentMood.note}
                  onChange={(e) => setCurrentMood({ ...currentMood, note: e.target.value })}
                  placeholder="Come ti sei sentito oggi?"
                  className="rounded-sm min-h-[80px]"
                />
                <Button 
                  onClick={handleNoteSave} 
                  className="w-full mt-2 rounded-sm"
                  data-testid="save-mood-note-btn"
                  disabled={!currentMood.level}
                >
                  Salva Nota
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Monthly Stats */}
          <Card className="border border-border shadow-sm rounded-sm" data-testid="mood-stats">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-lg">Statistiche del Mese</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center mb-4">
                <p className="text-3xl font-mono font-medium">{averageMood}</p>
                <p className="text-sm text-muted-foreground">Media mensile</p>
              </div>

              <div className="space-y-2">
                {moodStats.map(({ level, emoji, label, count }) => (
                  <div key={level} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{emoji}</span>
                      <span className="text-muted-foreground">{label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-foreground rounded-full transition-all"
                          style={{ width: `${filteredMoods.length > 0 ? (count / filteredMoods.length) * 100 : 0}%` }}
                        />
                      </div>
                      <span className="font-mono w-6 text-right">{count}</span>
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-xs text-muted-foreground text-center mt-4">
                {filteredMoods.length} giorni registrati su {daysInMonth}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Charts Section */}
      {filteredMoods.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Line Chart - Mood Trend */}
          <Card className="border border-border shadow-sm rounded-sm">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Andamento Umore</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={moodChartData}>
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis domain={[1, 5]} ticks={[1, 2, 3, 4, 5]} tick={{ fontSize: 12 }} />
                    <Tooltip 
                      formatter={(value) => [MOOD_LEVELS[value - 1]?.label, 'Umore']}
                      labelFormatter={(label) => `Giorno ${label}`}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="level" 
                      stroke="#1C1917" 
                      strokeWidth={2}
                      dot={{ fill: '#1C1917', strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Bar Chart - Mood Distribution */}
          <Card className="border border-border shadow-sm rounded-sm">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Distribuzione Umore</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={MOOD_LEVELS.map(m => ({
                    name: m.emoji,
                    count: filteredMoods.filter(mood => mood.level === m.level).length,
                    level: m.level
                  }))}>
                    <XAxis dataKey="name" tick={{ fontSize: 18 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip 
                      formatter={(value) => [value, 'Giorni']}
                      labelFormatter={(label) => MOOD_LEVELS.find(m => m.emoji === label)?.label}
                    />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                      {MOOD_LEVELS.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={['#DC2626', '#F97316', '#EAB308', '#22C55E', '#10B981'][index]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <DisclaimerFooter />
    </div>
  );
};

export default MoodPage;
